;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();import React from 'react';
import Link from 'next/link';
import { Col, Container, Row } from 'reactstrap';
import { MdBusiness, MdOutlineFileCopy, MdOutlineSend, MdPersonPin, MdWhatsapp } from 'react-icons/md';
import { useSessionStorage } from 'usehooks-ts';
import { useUser } from 'shared/contexts/UserContext';
import ThankyouHeaderImage from '../svg/ThankyouHeader';
import capitalizeFirstLetter from '../../utils/capitalizeFirstLetter';
import Loading from '../Loading';
import { Dictionaries, Locale } from '../../i18n/i18n-config';
import { getI18nUtils } from '../../i18n/i18n-utils';

const mySynergieEnabled = process.env.NEXT_PUBLIC_MYSYNERGIE === 'true';

type Props = {
  dictionary: Partial<Dictionaries>;
  job: Job | null;
  locale: Locale;
};

const HeaderSection = ({ dictionary, job, locale }: Props): JSX.Element => {
  const { formatMessage } = getI18nUtils(dictionary);
  const { candidate, loading } = useUser();
  const [sessionLastApplication] = useSessionStorage<string>('latestJobApplication', '{}');
  const [sessionJobFacetFilters] = useSessionStorage('jobFacetFilters', null);

  const candidateData: any = candidate || JSON.parse(sessionLastApplication);

  let cvLabel: string | null = 'thankyou_header_cv_missing'; // Default: No CV available
  if (candidateData?.cvDate)
    // New CV added during application
    cvLabel = 'cv_added_to_profile';
  else if (candidateData?.cv)
    // Existing CV added from profile: show no label
    cvLabel = null;

  if (loading) return <Loading />;

  return (
    <Container id="header">
      <Row>
        <Col lg={{ size: 5, order: 2 }} className="d-flex justify-content-center align-items-center">
          <ThankyouHeaderImage height={200} width={200} />
        </Col>
        <Col lg={{ size: 7, order: 1 }} className="p-5">
          <div className="mx-xl-5">
            <h1 className="mb-4" style={{ textTransform: 'none' }}>
              {formatMessage({ id: 'thankyou_header_title' })} {candidateData?.firstName || ''}
            </h1>
            {job?.title && (
              <p className="mb-3">
                {formatMessage(
                  { id: 'thankyou_header_application_received' },
                  { vacancyTitle: capitalizeFirstLetter(job.title.trim()) }
                )}
              </p>
            )}
            <p className="mb-3">{formatMessage({ id: 'thankyou_header_furthermore' })}</p>
            <ul className="list-unstyled">
              {candidate?.email && (
                <li className="d-flex mb-3">
                  <div className="d-inline-block icon-container">
                    <MdOutlineSend size={20} color="#717F8A" />
                  </div>
                  <span className="d-inline-block ms-3">
                    {formatMessage({ id: 'thankyou_header_expect_mail' }, { candidateEmail: candidate.email })}
                  </span>
                </li>
              )}
              {job?.agency && (
                <li className="d-flex mb-3">
                  <div className="d-inline-block icon-container">
                    <MdBusiness size={20} color="#717F8A" />
                  </div>
                  <span className="d-inline-block ms-3">
                    {`${formatMessage({ id: 'thankyou_header_agency_pt1' })}`}
                    <a href={`/${locale}/${job.uri}`} target="_blank" rel="noopener noreferrer">
                      {job.agency.title}
                    </a>
                    {formatMessage({ id: 'thankyou_header_agency_pt2' })}
                  </span>
                </li>
              )}
              {mySynergieEnabled && candidate?.id && (
                <>
                  <li className="d-flex mb-3">
                    <div className="d-inline-block icon-container">
                      <MdPersonPin size={20} color="#717F8A" />
                    </div>
                    <span className="d-inline-block ms-3">
                      {formatMessage(
                        { id: 'application_added_mysynergie' },
                        {
                          mySynergieLink: <Link href={`/${locale}/my-synergie/job-applications`}>My Synergie</Link>,
                        }
                      )}
                    </span>
                  </li>
                  {cvLabel && (
                    <li className="d-flex mb-3">
                      <div className="d-inline-block icon-container">
                        <MdOutlineFileCopy size={20} color="#717F8A" />
                      </div>
                      <span className="d-inline-block ms-3">
                        {formatMessage(
                          { id: cvLabel },
                          {
                            profileLink: (
                              <Link href={`/${locale}/my-synergie/profile/resume`} locale={locale}>
                                {formatMessage({ id: 'thankyou_header_profile' })}
                              </Link>
                            ),
                          }
                        )}
                      </span>
                    </li>
                  )}
                </>
              )}
              <li className="d-flex mb-3">
                <div className="d-inline-block icon-container">
                  <MdWhatsapp size={20} color="#717F8A" />
                </div>
                <span className="d-inline-block ms-3">
                  {formatMessage(
                    { id: 'thankyou_header_whatsapp' },
                    {
                      whatsappLink: (
                        <a href="https://www.optin.social/synergie" target="_blank" rel="noopener noreferrer">
                          {formatMessage({ id: 'thankyou_header_whatsapp_link' })}
                        </a>
                      ),
                    }
                  )}
                </span>
              </li>
            </ul>
          </div>
          <div className="mx-xl-5">
            <Link
              className="mt-3 me-3 btn btn-primary"
              href={`/${locale}/jobs/${sessionJobFacetFilters ? `?${sessionJobFacetFilters}` : ''}`}
            >
              {formatMessage({ id: 'continue_applying' })}
            </Link>
            {!candidate?.id && mySynergieEnabled && (
              <Link
                className="mt-3 btn btn-outline-primary"
                href={`/auth/signup${candidate?.email ? `?email=${candidate.email}` : ''}`}
              >
                {formatMessage({ id: 'signup' })}
              </Link>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
};
export default HeaderSection;
